import { useCallback, useEffect, useRef, useState } from 'react';
import { getPatientEligibilityProblemsEligibilityPatientPatientIdProblemsGet } from '../services/api/sdk.gen';
import type { EligibilityProblem } from '../services/api/types.gen';

export interface UsePatientEligibilityProblemsResult {
  errors: EligibilityProblem[];
  errorCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Patient-scoped eligibility problems for the Insurance tab red badge.
 *
 * Mirrors `useAuthorizationBudget`'s request-id guard so a patient switch
 * cannot paint the previous patient's warnings on the new chart.
 */
export const usePatientEligibilityProblems = (
  patientId: string | null | undefined
): UsePatientEligibilityProblemsResult => {
  const [errors, setErrors] = useState<EligibilityProblem[]>([]);
  const [errorCount, setErrorCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);
  const onScreenRef = useRef(false);

  const reset = useCallback(() => {
    setErrors([]);
    setErrorCount(0);
    onScreenRef.current = false;
  }, []);

  const refresh = useCallback(async () => {
    const requestId = ++requestRef.current;
    if (!patientId) {
      reset();
      setLoading(false);
      return;
    }
    if (!onScreenRef.current) {
      setLoading(true);
    }
    try {
      const res =
        await getPatientEligibilityProblemsEligibilityPatientPatientIdProblemsGet(
          { path: { patient_id: patientId }, throwOnError: true }
        );
      if (requestId !== requestRef.current) return;
      setErrors(res.data.errors);
      setErrorCount(res.data.count);
      onScreenRef.current = true;
    } catch {
      if (requestId !== requestRef.current) return;
      // Soft-fail: do not invent a badge from a failed fetch.
      reset();
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [patientId, reset]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { errors, errorCount, loading, refresh };
};
